"use client";

import { TrialDto } from "@decibeltrade/sdk";
import { useEffect } from "react";

import { FFT_SETTLE_OBSERVED_KEY } from "@/constants/local-storage-keys";
import { useAnalytics } from "@/contexts/analytics-context";
import { getServerTime } from "@/utils/server-time";

/** Fires when the cached trial flips terminal (covers WS + poll); localStorage-deduped. Measures WITNESSING, not settlement. */
export function useTrackFftSettleObserved({
  trial,
  manualSettle,
  enabled,
}: {
  trial: TrialDto | null;
  /** True when this client's Settle Now mutation produced the terminal state. */
  manualSettle: boolean;
  enabled: boolean;
}) {
  const analytics = useAnalytics();

  useEffect(() => {
    if (!enabled || !trial || trial.status === "Active") return;
    if (!analytics.rudder) return;
    if (readLastObserved() === String(trial.trial_id)) return;
    writeLastObserved(String(trial.trial_id));
    analytics.fft.settleObserved({
      trialId: trial.trial_id,
      status: trial.status,
      settleReason: trial.settle_reason,
      userPayoutUsd: trial.user_payout_usd,
      protectedAmountUsd: trial.protected_amount_usd,
      observedVia: manualSettle ? "manual" : "push",
      trialDurationMs:
        trial.closed_at_ms !== undefined && trial.opened_at_ms !== undefined
          ? trial.closed_at_ms - trial.opened_at_ms
          : undefined,
      timeToObserveMs:
        trial.closed_at_ms !== undefined
          ? Math.max(0, getServerTime() - trial.closed_at_ms)
          : undefined,
    });
  }, [enabled, trial, manualSettle, analytics]);
}

// Guarded reads/writes — localStorage throws in Safari private mode.
function readLastObserved(): string | null {
  try {
    return localStorage.getItem(FFT_SETTLE_OBSERVED_KEY);
  } catch {
    return null;
  }
}

function writeLastObserved(trialId: string) {
  try {
    localStorage.setItem(FFT_SETTLE_OBSERVED_KEY, trialId);
  } catch {
    // Dedupe degrades to per-mount; the event still fires.
  }
}
